import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Grid,
  Chip,
  Divider,
  IconButton,
  useTheme,
  CircularProgress,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import FavoriteIcon from "@mui/icons-material/Favorite";
import StarIcon from "@mui/icons-material/Star";
import FamilyIcon from "@mui/icons-material/FamilyRestroom";
import CakeIcon from "@mui/icons-material/Cake";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HeightIcon from "@mui/icons-material/Height";
import ScaleIcon from "@mui/icons-material/Scale";
import LanguageIcon from "@mui/icons-material/Language";
import PublicIcon from "@mui/icons-material/Public";
import BusinessIcon from "@mui/icons-material/Business";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PsychologyIcon from "@mui/icons-material/Psychology";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import MovieIcon from "@mui/icons-material/Movie";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { useState, useEffect } from "react";
import masterService from "../services/masterService";

const UserDetailsModal = ({ open, onClose, user, theme, loading = false }) => {
  const [masterData, setMasterData] = useState({
    countries: [],
    states: [],
    cities: [],
    qualifications: [],
    raasis: [],
    castes: [],
    nakshatras: [],
    padams: [],
    casteCategories: [],
    callTimes: [],
    complexions: [],
    genderOptions: [
      { id: "male", name: "Male" },
      { id: "female", name: "Female" },
      { id: "other", name: "Other" },
    ],
    maritalStatusOptions: [
      { id: "single", name: "Single" },
      { id: "married", name: "Married" },
      { id: "divorced", name: "Divorced" },
      { id: "widowed", name: "Widowed" },
    ],
    incomeOptions: [
      { id: "less_than_10l", name: "< 10L" },
      { id: "10_to_30l", name: "10-30L" },
      { id: "30_to_50l", name: "30-50L" },
      { id: "more_than_50l", name: "> 50L" },
    ],
    flatOptions: [
      { id: "highrise", name: "Highrise" },
      { id: "lowrise", name: "Lowrise" },
    ],
    landOptions: [
      { id: "agricultural", name: "Agricultural" },
      { id: "residential", name: "Residential" },
      { id: "commercial", name: "Commercial" },
    ],
    heightOptions: [
      { id: "5ft 2in - 157cm", name: "5ft 2in - 157cm" },
      { id: "5ft 3in - 160cm", name: "5ft 3in - 160cm" },
      { id: "5ft 4in - 162cm", name: "5ft 4in - 162cm" },
      { id: "5ft 5in - 165cm", name: "5ft 5in - 165cm" },
      { id: "5ft 6in - 167cm", name: "5ft 6in - 167cm" },
      { id: "5ft 7in - 170cm", name: "5ft 7in - 170cm" },
      { id: "5ft 8in - 172cm", name: "5ft 8in - 172cm" },
      { id: "5ft 9in - 175cm", name: "5ft 9in - 175cm" },
      { id: "5ft 10in - 177cm", name: "5ft 10in - 177cm" },
      { id: "5ft 11in - 180cm", name: "5ft 11in - 180cm" },
      { id: "6ft - 182cm", name: "6ft - 182cm" },
      { id: "6ft 1in - 185cm", name: "6ft 1in - 185cm" },
      { id: "6ft 2in - 187cm", name: "6ft 2in - 187cm" },
    ],
    yesNoOptions: [
      { id: "Yes", name: "Yes" },
      { id: "No", name: "No" },
    ],
  });
  const [loadingMasterData, setLoadingMasterData] = useState(false);

  // Fetch master data when modal opens
  useEffect(() => {
    if (open && user) {
      fetchMasterData();
    }
  }, [open, user]);

  const fetchMasterData = async () => {
    setLoadingMasterData(true);
    try {
      const [
        countries,
        qualifications,
        raasis,
        castes,
        nakshatras,
        padams,
        casteCategories,
        callTimes,
        complexions,
      ] = await Promise.all([
        masterService.getCountryList(),
        masterService.getQualificationList(),
        masterService.getRaasiList(),
        masterService.getCasteList(),
        masterService.getNakshatraList(),
        masterService.getCasteCategoryList(),
        masterService.getPreferredCallingTimes(),
        masterService.getComplexionList(),
      ]);

      setMasterData(prev => ({
        ...prev,
        countries,
        qualifications,
        raasis,
        castes,
        nakshatras,
        padams: [],
        casteCategories,
        callTimes,
        complexions,
        states: [],
        cities: [],
      }));

      // Fetch states if country is available
      if (user.country) {
        const states = await masterService.getStatesList(user.country);
        setMasterData(prev => ({ ...prev, states }));
      }
      if(user.state){
        const cities = await masterService.getCitiesList(user.state);
        setMasterData(prev => ({ ...prev, cities }));
      }

      // Fetch padams if nakshatra is available
      if (user.nakshatra) {
        try {
          const padams = await masterService.getPadamList(user.nakshatra);
          setMasterData(prev => ({ ...prev, padams }));
        } catch (error) {
          console.error("Error loading padams:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching master data:", error);
    } finally {
      setLoadingMasterData(false);
    }
  };

  // Helper function to safely display user data
  const safeDisplay = (value, fallback = "Not provided") => {
    if (value === null || value === undefined || value === "") return fallback;
    return value;
  };

  // Helper function to get name from ID
  const getNameFromId = (id, dataArray, fieldName = "name") => {
    if (!id || !dataArray || !Array.isArray(dataArray)) return id;
    const item = dataArray.find(item => item.id === String(id));
    return item ? item[fieldName] : id;
  };

  const handleClose = () => {
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Clean Header */}
      <DialogTitle
        sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Profile Details
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading || loadingMasterData ? (
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            minHeight: 300,
            p: 4
          }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            {/* Profile Header */}
            <Box sx={{ mb: 4, textAlign: "center" }}>
              <Avatar
                src={
                  user.image
                    ? user.image.startsWith('http')
                      ? user.image
                      : `${process.env.REACT_APP_IMAGE_BASE_URL}/${user.image}`
                    : undefined
                }
                sx={{
                  width: 120,
                  height: 120,
                  border: `3px solid ${theme.palette.primary.main}`,
                  mx: "auto",
                  mb: 2,
                }}
              />
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
                {safeDisplay(user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim(), "Name not provided")}
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                {user.age && `${user.age} years`} {user.location && `• ${user.location}`}
              </Typography>
              <Chip
                label={`${user.liked_count || 0} Likes`}
                icon={<FavoriteIcon />}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: "white",
                  fontWeight: 500,
                }}
              />
            </Box>

                        {/* Information Grid - Order Wise */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1,
              '& > *': {
                flex: '0 0 calc(20% - 8px)', // Exactly 5 items per row (100% / 5 = 20%)
                minWidth: '200px', // Minimum width for readability
                '@media (max-width: 960px)': {
                  flex: '0 0 calc(50% - 8px)', // 2 items per row on medium screens
                },
                '@media (max-width: 600px)': {
                  flex: '0 0 100%', // 1 item per row on small screens
                }
              }
            }}>
              {/* Row 1: Basic Contact Info */}
              {user.email && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <EmailIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )}
              {user.mobile && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PhoneIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Phone
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.mobile}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.gender && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Gender
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.gender, masterData.genderOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.date_of_birth && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <CakeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Date of Birth
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {new Date(user.date_of_birth).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.age && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Age
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.age} years
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}

                            {/* Row 2: Location Info */}
              {user.country && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PublicIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Country
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.country, masterData.countries)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.state && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <LocationOnIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          State
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.state, masterData.states)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.city && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <HomeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          City
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.city, masterData.cities)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.birth_place && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <LocationOnIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Birth Place
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.birth_place}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.family_location && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <HomeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Family Location
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.family_location}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}

                            {/* Row 3: Birth Details */}
              {user.birth_time && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <AccessTimeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Birth Time
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.birth_time}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.raasi && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <StarIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Raasi
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.raasi, masterData.raasis)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.nakshatra && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <StarIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Nakshatra
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.nakshatra, masterData.nakshatras)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.padam && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <StarIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Padam
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.padam, masterData.padams)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.gothram && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <StarIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Gothram
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.gothram}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}

              {/* Row 4: Professional Info */}
              {user.occupation && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <WorkIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Occupation
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.occupation}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.company && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <BusinessIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Company
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.company}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.education && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <SchoolIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Education
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.education}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.qualification && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <AccountBalanceIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Qualification
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.qualification, masterData.qualifications)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.income && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <WorkIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Annual Income
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.income, masterData.incomeOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}

                            {/* Row 5: Physical & Personal Details */}
              {user.height_cm && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <HeightIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Height
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.height_cm, masterData.heightOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.blood_group && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Blood Group
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.blood_group}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.complexion && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Complexion
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.complexion, masterData.complexions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.marital_status && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FavoriteBorderIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Marital Status
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.marital_status, masterData.maritalStatusOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.manglik && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <StarIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Manglik
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.manglik}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}

              {/* Row 6: Family Info */}
              {user.father_name && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Father's Name
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.father_name}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.mother_name && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Mother's Name
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.mother_name}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.religion && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PublicIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Religion
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.religion}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.father_occupation && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <WorkIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Father's Occupation
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.father_occupation}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.mother_occupation && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <WorkIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Mother's Occupation
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.mother_occupation}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}

              {/* Row 7: Siblings & Caste Info */}
              {user.siblings_info && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FamilyIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Siblings Information
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.siblings_info}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.brother_count && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Brothers Count
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.brother_count}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.sister_count && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Sisters Count
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.sister_count}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.caste && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PublicIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Caste
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.caste, masterData.castes)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.caste_category && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <PublicIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Caste Category
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.caste_category, masterData.casteCategories)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}

                            {/* Row 8: Time & Property Details */}
              {user.preferred_call_time && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <AccessTimeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Preferred Call Time
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.preferred_call_time, masterData.callTimes)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.future_duration && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <AccessTimeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Future Duration
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.future_duration}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.flat && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <HomeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Flat Type
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.flat, masterData.flatOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.land && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <HomeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Land Type
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.land, masterData.landOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.rc_in_groom_style && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FavoriteIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          RC in Groom Style
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.rc_in_groom_style, masterData.yesNoOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}

              {/* Row 9: Style Preferences */}
              {user.em_in_groom_style && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FavoriteIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          EM in Groom Style
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.em_in_groom_style, masterData.yesNoOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.mw_in_bride_style && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FavoriteIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          MW in Bride Style
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.mw_in_bride_style, masterData.yesNoOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.hm_package && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FavoriteIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          HM Package
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.hm_package, masterData.yesNoOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.sil_wishList && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FavoriteIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          SIL WishList
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.sil_wishList, masterData.yesNoOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
              {user.silver_ware && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FavoriteIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Silver Ware
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.silver_ware, masterData.yesNoOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}

              {/* Row 10: Gold Jewelry */}
              {user.gold_jewelry && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FavoriteIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
                          Gold Jewelry
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getNameFromId(user.gold_jewelry, masterData.yesNoOptions)}
                        </Typography>
                      </Box>
                    </Box>
                </Paper>
              )}
            </Box>

            {/* About Section */}
            {user.about && (
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mt: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: theme.palette.text.primary, display: "flex", alignItems: "center", gap: 1 }}>
                      <PsychologyIcon sx={{ fontSize: 20 }} />
                      About
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.6, color: theme.palette.text.primary }}>
                      {user.about}
                    </Typography>
              </Paper>
            )}

            {/* Interests Section */}
            {user.interests && (
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mt: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: theme.palette.text.primary, display: "flex", alignItems: "center", gap: 1 }}>
                      <StarIcon sx={{ fontSize: 20 }} />
                      Interests
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {Array.isArray(user.interests) ? user.interests.map((interest, index) => (
                        <Chip
                          key={index}
                          label={interest}
                          size="small"
                          sx={{
                            bgcolor: theme.palette.primary.light,
                            color: theme.palette.primary.contrastText,
                            fontWeight: 500,
                          }}
                        />
                      )) : (
                        <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                          {user.interests}
                        </Typography>
                      )}
                    </Box>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailsModal;
